import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Sparkles, RefreshCw, ChevronLeft, ChevronRight, 
  FileText, Mic, Copy, Check 
} from "lucide-react";
// animations removed
import { toast } from "sonner";
// PDF export removed per user request

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const PresentationStep = ({ project, setProject, saveProject, goToStep, onFinalize }) => {
  const { t, language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [presentation, setPresentation] = useState(null);
  const [pitch, setPitch] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [copiedPitch, setCopiedPitch] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const slidesRef = useRef(null);

  useEffect(() => {
    // If user switches language, require regeneration to avoid mixed-language content
    setHasGenerated(false);
    setPresentation(null);
    setPitch("");
    setCurrentSlide(0);
  }, [language]);

  useEffect(() => {
    if (project?.presentation) {
      setPresentation(project.presentation);
      setPitch(project.pitch || project.presentation.elevator_pitch || "");
      setHasGenerated(true);
    }
  }, [project?.presentation, project?.pitch]);

  const generatePresentation = async () => {
    if (!project?.concept) {
      toast.error(t('common_error'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/generate/presentation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          problem_statement: project.problem_statement,
          selected_idea: project.selected_idea,
          value_proposition: project.value_proposition,
          business_model: project.business_model,
          language,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPresentation(data);
        setPitch(data.elevator_pitch || "");
        setHasGenerated(true);
        
        await saveProject({ 
          presentation: data,
          pitch: data.elevator_pitch,
          current_step: 7
        }, false);
        
        setProject(prev => ({ 
          ...prev, 
          presentation: data, 
          pitch: data.elevator_pitch 
        }));
        toast.success(t('common_success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common_error'));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t('common_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  // PDF export removed per user request

  const copyPitch = async () => {
    try {
      await navigator.clipboard.writeText(pitch);
      setCopiedPitch(true);
      toast.success(t('prototype_copied'));
      setTimeout(() => setCopiedPitch(false), 2000);
    } catch (error) {
      toast.error(t('common_error'));
    }
  };

  const nextSlide = () => {
    if (presentation?.slides && currentSlide < presentation.slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const slides = presentation?.slides || [];

  const copySlide = async (slide) => {
    if (!slide) return;
    const text = `${slide.title}\n\n${slide.content}\n\n${t('presentation_pitch_duration')}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('presentation_copied_slide'));
    } catch (error) {
      toast.error(t('common_error'));
    }
  };

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {t('presentation_title')}
            </h2>
            <p className="text-stone-600">
              {t('presentation_description')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generatePresentation}
              disabled={isGenerating}
              variant={hasGenerated ? "outline" : "default"}
              className={hasGenerated ? "btn-secondary" : "btn-primary"}
              data-testid="generate-presentation-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('common_generating')}
                </>
              ) : hasGenerated ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('presentation_regenerate')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('presentation_generate')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-8">
        <div className="max-w-5xl mx-auto">
          {!hasGenerated ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                {t('presentation_generate')}
              </h3>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                {t('presentation_description')}
              </p>
              <Button
                onClick={generatePresentation}
                disabled={isGenerating}
                className="btn-primary"
                data-testid="generate-presentation-empty-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common_generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('presentation_generate')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Slides */}
              <div ref={slidesRef}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold text-stone-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    {t('presentation_slides')}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={prevSlide}
                      disabled={currentSlide === 0}
                      data-testid="prev-slide-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <span className="text-sm text-stone-600">
                      {currentSlide + 1} / {slides.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={nextSlide}
                      disabled={currentSlide === slides.length - 1}
                      data-testid="next-slide-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Slide Display */}
                <div className="relative overflow-hidden rounded-2xl shadow-floating bg-white" data-testid="slide-display">
                  {/* Brand header */}
                  <div className="bg-primary px-6 py-3 flex items-center justify-between gap-4">
                    <span className="text-white text-sm font-medium">Voltea Ideas</span>
                    {slides[currentSlide] && (
                      <Button
                        onClick={() => copySlide(slides[currentSlide])}
                        size="sm"
                        variant="outline"
                        className="bg-white/10 hover:bg-white/20 text-white border-white/40 text-xs"
                      >
                        {copiedPitch ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            {t('prototype_copied')}
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 mr-1" />
                            {t('prototype_copy')}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  {slides[currentSlide] && (
                    <div className="presentation-slide">
                      <h2 className="slide-title">{slides[currentSlide].title}</h2>
                      <p className="slide-content">{slides[currentSlide].content}</p>
                      <div className="slide-key-point">
                        <span className="text-stone-500">
                          {language === 'es'
                            ? 'Punto clave:'
                            : language === 'fr'
                            ? 'Point clé :'
                            : language === 'it'
                            ? 'Punto chiave:'
                            : 'Key point:'}
                        </span>{' '}
                        {slides[currentSlide].key_point}
                      </div>
                    </div>
                  )}
                </div>

                {/* Slide Thumbnails */}
                <div className="flex gap-2 mt-4 overflow-x-auto py-2">
                  {slides.map((slide, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`flex-shrink-0 w-24 h-16 rounded-lg border-2 p-2 transition-all ${
                        index === currentSlide
                          ? "border-primary bg-primary-light"
                          : "border-stone-200 bg-white hover:border-stone-300"
                      }`}
                      data-testid={`slide-thumb-${index}`}
                    >
                      <div className="text-[8px] font-medium text-stone-700 line-clamp-1">
                        {slide.title}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Elevator Pitch */}
              <Card className="pitch-card" data-testid="pitch-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
                    <Mic className="w-5 h-5" />
                    {t('presentation_pitch')}
                  </h3>
                  <Button
                    onClick={copyPitch}
                    variant="secondary"
                    size="sm"
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    data-testid="copy-pitch-btn"
                  >
                    {copiedPitch ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {t('prototype_copied')}
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        {t('prototype_copy')}
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-lg leading-relaxed text-white/90">
                  "{pitch}"
                </p>
                <div className="mt-4 text-sm text-white/60">
                  {t('presentation_pitch_duration')}
                </div>
              </Card>

              {/* Completion Message + Finalize */}
              <Card className="bg-accent-green p-6 border-green-200 text-center space-y-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-green-900 mb-2">
                  {t('presentation_complete_title')}
                </h3>
                <p className="text-green-800">
                  {t('presentation_complete_desc')}
                </p>
                <div className="pt-2">
                  <Button
                    className="btn-primary px-8"
                    onClick={async () => {
                      if (!onFinalize) return;
                      setIsFinalizing(true);
                      try {
                        await onFinalize();
                        toast.success(t('completion_finalized'));
                      } catch (error) {
                        console.error('Finalize error:', error);
                        toast.error(t('common_error'));
                      } finally {
                        setIsFinalizing(false);
                      }
                    }}
                    disabled={isFinalizing}
                    data-testid="finalize-project-btn"
                  >
                    {isFinalizing ? t('common_saving') : t('completion_finalize_button')}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PresentationStep;
